import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Radio, Loader2, ShieldAlert } from "lucide-react";

const BG_URL = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjB2aXN1YWxpemF0aW9uJTIwbGluZXN8ZW58MHx8fHwxNzgwNDQ0NDM3fDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@gardamedia.id");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-slate-50">
      <div
        className="hidden md:flex relative items-end p-10 bg-slate-900 text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.85), rgba(15,23,42,0.85)), url(${BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-sm bg-white text-slate-900 flex items-center justify-center">
              <Radio className="h-5 w-5" />
            </div>
            <p className="text-lg font-bold tracking-tight">GardaMedia</p>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">
            Monitoring siaran berita TV digital secara presisi.
          </h1>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">
            Transkripsi otomatis Bahasa Indonesia dengan AI Whisper, tagging
            keyword pertahanan, dan pencarian full-text untuk operasional intelijen media.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3 text-xs">
            <div className="border border-white/15 rounded-sm p-3">
              <p className="font-mono text-amber-300">01</p>
              <p className="mt-1 text-slate-200">Ingest AVI</p>
            </div>
            <div className="border border-white/15 rounded-sm p-3">
              <p className="font-mono text-amber-300">02</p>
              <p className="mt-1 text-slate-200">Whisper STT</p>
            </div>
            <div className="border border-white/15 rounded-sm p-3">
              <p className="font-mono text-amber-300">03</p>
              <p className="mt-1 text-slate-200">Keyword Hits</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm bg-white border border-slate-200 rounded-sm p-8 shadow-sm"
          data-testid="login-form"
        >
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Akses Operator</p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight">Masuk ke sistem</h2>
          <p className="mt-2 text-sm text-slate-500">
            Hanya untuk personel terotorisasi.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 rounded-sm border-slate-300 focus-visible:ring-slate-900"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-600">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 rounded-sm border-slate-300 focus-visible:ring-slate-900"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-xs text-red-800" data-testid="login-error">
                <ShieldAlert className="h-4 w-4 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none"
              data-testid="login-submit-button"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Masuk
            </Button>

            <p className="text-[11px] text-slate-500 leading-relaxed">
              Demo: <span className="font-mono">admin@gardamedia.id / admin123</span><br />
              Analis: <span className="font-mono">analis@gardamedia.id / analis123</span>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
